/* eslint-disable no-case-declarations */
import React from 'react';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import { CircularProgress, Checkbox, Typography, Tooltip } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CustomToolbar from '../CustomToolbar';
import globals from '../../../../globals/communityGlobals';
import useTeamProvider from '../useTeamProvider';
import _ from '../../../../utils/lodash';

const LightTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 12,
  },
}))(Tooltip);

const mapPropName = (columnName, isEntityToDto = false) => {
  const entityNames = ['PatientInfo', 'EventType', 'Status', 'SentDate'];
  const dtoNames = ['patientInfo', 'eventType', 'statusName', 'sentDate'];
  if (isEntityToDto) {
    const index = _.indexOf(entityNames, columnName);
    return dtoNames[index];
  }
  const index = _.indexOf(dtoNames, columnName);
  return entityNames[index];
};

const setSortDirection = (columnDtoName, options) => {
  const entityName = mapPropName(columnDtoName, false);

  return entityName === options.sortColumn ? options.sortDirection : 'none';
};

export const tableSettings = options => {
  const {
    epRequestEnums,
    selectedAllForSending,
    requestIds,
    actionName,
    handleCheckAllForSending,
    handleSingleCheck,
    handleDetailsSidePanel,
    onOpenConfirmationDialog,
    updateTableSort,
    updateTableProps,
  } = useTeamProvider();

  const getStatusName = statusName => {
    return epRequestEnums.find(s => s.value === statusName).name;
  };

  const columns = [
    {
      name: 'Select',
      options: {
        filter: false,
        sort: false,
        empty: true,
        customHeadRender: () => {
          return (
            <Checkbox
              color="primary"
              onChange={event => handleCheckAllForSending(event.target.checked)}
              checked={selectedAllForSending === true}
            />
          );
        },
        customBodyRender: (value, tableMeta) => {
          const btnName = getStatusName(tableMeta.rowData[6]);
          return (
            btnName === epRequestEnums[1].name && (
              <Checkbox
                checked={selectedAllForSending === true || requestIds.includes(tableMeta.rowData[1])}
                color="primary"
                className={'custom-checkbox-cell-render'}
                onChange={event => handleSingleCheck(event.target.checked, tableMeta.rowData[1])}
              />
            )
          );
        },
      },
    },
    {
      name: 'id',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'assignmentId',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'serviceAssignmentId',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'patientInfo',
      label: 'Patient',
      options: {
        sortOrder: {
          name: 'patientInfo',
          direction: setSortDirection('patientInfo', options),
        },
      },
    },
    {
      name: 'eventType',
      label: 'Event Type',
      options: {
        sortOrder: {
          name: 'eventType',
          direction: setSortDirection('eventType', options),
        },
      },
    },
    {
      name: 'statusName',
      label: 'Status',
      options: {
        sortOrder: {
          name: 'statusName',
          direction: setSortDirection('statusName', options),
        },
        customBodyRender: value => {
          return <span>{getStatusName(value)}</span>;
        },
      },
    },
    {
      name: 'failedMessage',
      label: 'Error Message',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          return (
            <div onMouseEnter={options.onHover} onMouseLeave={options.onLeave}>
              <LightTooltip title={tableMeta.rowData[7] ? tableMeta.rowData[7] : ''}>
                <span className={'custom-cell-render'}>{tableMeta.rowData[7] ? tableMeta.rowData[7] : '-'}</span>
              </LightTooltip>
            </div>
          );
        },
      },
    },
    {
      name: 'createdDate',
      label: 'Created Date',
      options: {
        sortOrder: {
          name: 'createdDate',
          direction: setSortDirection('createdDate', options),
        },
        customBodyRender: (value, tableMeta) => {
          return tableMeta.rowData[8] ? moment(value).format(globals.dateFormats.shortWithLongTime) : '-';
        },
      },
    },
    {
      name: 'sentDate',
      label: 'Sent Date',
      options: {
        sortOrder: {
          name: 'sentDate',
          direction: setSortDirection('sentDate', options),
        },
        customBodyRender: (value, tableMeta) => {
          return tableMeta.rowData[9] ? moment(value).format(globals.dateFormats.shortWithLongTime) : '-';
        },
      },
    },
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        empty: true,
        setCellProps: () => ({ style: { width: '200px' } }),
        customBodyRender: (value, tableMeta) => {
          const btnName = getStatusName(tableMeta.rowData[6]);
          return (
            <div>
              {tableMeta.rowData[3] !== null &&
                btnName === epRequestEnums[2].name &&
                tableMeta.rowData[5] === 'Create Visit' && (
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => {
                      handleDetailsSidePanel(true, tableMeta.rowData[3], tableMeta.rowData[2], tableMeta.rowData[4]);
                    }}
                  >
                    {'Details'}
                  </Button>
                )}
              {btnName === epRequestEnums[1].name && (
                <React.Fragment>
                  {requestIds.includes(tableMeta.rowData[1]) && actionName === 'Send' ? (
                    <Button size="small" className="search-icon">
                      <CircularProgress size={18} color="inherit" />
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => onOpenConfirmationDialog('Send', tableMeta.rowData[1])}
                    >
                      {'Send'}
                    </Button>
                  )}
                </React.Fragment>
              )}
              {btnName === epRequestEnums[3].name && btnName === epRequestEnums[3].name && (
                <React.Fragment>
                  {requestIds.includes(tableMeta.rowData[1]) && actionName === 'Resend' ? (
                    <Button size="small" className="search-icon">
                      <CircularProgress size={18} color="inherit" />
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => onOpenConfirmationDialog('Resend', tableMeta.rowData[1])}
                    >
                      {'Resend'}
                    </Button>
                  )}
                  &nbsp;|&nbsp;
                  {requestIds.includes(tableMeta.rowData[1]) && actionName === 'Discard' ? (
                    <Button size="small" className="search-icon">
                      <CircularProgress size={18} color="inherit" />
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => onOpenConfirmationDialog('Discard', tableMeta.rowData[1])}
                    >
                      {'Discard'}
                    </Button>
                  )}
                </React.Fragment>
              )}
            </div>
          );
        },
      },
    },
  ];

  return {
    name: <Typography variant="h3">Provider Request Logs</Typography>,
    columns,
    options: {
      filterType: 'dropdown',
      responsive: 'standard',
      selectableRows: 'none',
      rowsPerPageOptions: [5, 15, 30, 50, 100],
      rowsPerPage: options.pageSize,
      print: false,
      search: false,
      filter: false,
      serverSide: true,
      count: options.count,
      page: options.page,
      customToolbar: () => <CustomToolbar isTableDataLoading={options.isTableDataLoading} />,
      onTableChange: (action, tableState) => {
        switch (action) {
          case 'changePage':
          case 'changeRowsPerPage':
            updateTableProps(tableState.page + 1, tableState.rowsPerPage);
            break;
          case 'sort':
            let sortColumn = '';
            let sortDirection = '';

            if (tableState.activeColumn > -1) {
              sortColumn = mapPropName(tableState.sortOrder.name);
              sortDirection = tableState.sortOrder.direction;
              updateTableSort(sortColumn, sortDirection);
            }
            break;
          default:
            break;
        }
      },
    },
  };
};
